// advisor-information.component.ts
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MiPerfilService } from '../../../services/mi-perfil/mi-perfil.service';
import { CatalogParamsService } from '@modules/shared/services/catalog-params.service';
import { AdicionalesService } from 'src/app/services/catalogos/adicionales.service';
import { DialogService } from 'src/app/services/dialog.service';
import { CustomValidators } from 'src/app/proveiders/CustomValidators';
import { firstValueFrom } from 'rxjs';

interface UnifiedProfile {
  bayport: {
    tipoEjecutivo: number;
    estado: string;
    coordinadorComercial: string;
    regional: number;
    codigoAsesor: number;
    fechaCreacion: string;
    sucursal: number;
    correo: string;
    tipoCanal: number;
    canalVenta: number;
  };
  personal: {
    tipoDocumento: number;
    numeroDocumento: string;
    primerNombre: string;
    segundoNombre: string;
    primerApellido: string;
    segundoApellido: string;
    fechaExpedicionDocumento: string;
    lugarExpedicionDocumento: string;
    fechaNacimiento: string;
    lugarNacimiento: string;
    departamento: number;
    ciudad: number;
    direccion: string;
    estadoCivil: number;
    estudiosRealizados: number;
    tipoVivienda: number;
    genero: number;
    correo: string;
    celular: string;
    personasACargo: number;
  };
  social: any;
  financial: any;
  banking: any;
}

@Component({
  selector: 'advisor-information',
  templateUrl: './advisor-information.component.html',
  styleUrls: ['../mi-perfil-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvisorInformationComponent implements OnInit {
  bayportInfoForm: FormGroup;

  executiveTypes: Array<{ indTipoEjecutivo: number; descripcion: string }> = [];
  regionalList: Array<{ codigoRegional: number; nombre: string }> = [];
  sucursales: Array<{ codigoSucursal: number; nombreSucursal: string }> = [];
  channelTypes: Array<{ codCnlVta: number; nomCnlVta: string }> = [];
  salesChannels: Array<{ codigoTipo: number; nombreTipo: string }> = [];

  tipoEjecDes = '';
  codigoAsesor = '';
  estado = '';
  fechaCrea!: Date;
  regionalNombre = '';
  sucursalNombre = '';
  canalNombre = '';
  canalVentaNombre = '';
  nombreRefiere = '';
  colorAdvertencia: "success" | "warning" | "info" = 'success';
  textoAdvertencia = 'La información fue guardada exitosamente';
  showAdvertencia = false;
  loadingAdvertencia = false;
  private unified!: UnifiedProfile;

  constructor(
    private fb: FormBuilder,
    private perfilService: MiPerfilService,
    private paramsService: CatalogParamsService,
    private adicionalesService: AdicionalesService,
    private dialog: DialogService,
    private cd: ChangeDetectorRef
  ) {
    this.bayportInfoForm = this.fb.group({
      executiveCode: [{ value: '', disabled: true }, Validators.required],
      executiveType: [{ value: '', disabled: true }, Validators.required],
      creationDate: [{ value: '', disabled: true }, Validators.required],
      state: [{ value: '', disabled: true }, Validators.required],
      regional: [{ value: '', disabled: true }, Validators.required],
      branch: [{ value: '', disabled: true }, Validators.required],
      commertialCoordinator: [{ value: '', disabled: true }, Validators.required],
      correo: ['', [Validators.required, Validators.email]],
      correo2: ['', [Validators.required, Validators.email]],
      channelType: [{ value: '', disabled: true }, Validators.required],
      salesChannel: [{ value: '', disabled: true }, Validators.required],
      celular: [
        null,
        [
          Validators.required,
          Validators.maxLength(10),
          CustomValidators.patternValidator(/^3/, { numberThree: true }),
          Validators.pattern(/^[0-9]+$/),
        ],
      ],
    });
  }

  async ngOnInit(): Promise<void> {
    await this.loadCatalogs();
    await this.loadAdvisorInfo();
  }

  private async loadCatalogs(): Promise<void> {
    const [execRes, regRes, sucRes, chanRes, salesRes] = await Promise.all([
      this.paramsService.getExecutiveType().toPromise(),
      this.paramsService.getRegional().toPromise(),
      this.adicionalesService.getSucursales().toPromise(),
      this.paramsService.getChannelType().toPromise(),
      this.paramsService.getSalesChannel().toPromise().catch(() => ({ rows: [] })),
    ]);

    this.executiveTypes = execRes.rows;
    this.regionalList = regRes.rows;
    this.sucursales = sucRes.rows;
    this.channelTypes = chanRes.rows;
    this.salesChannels = salesRes.rows || [];
  }

  private async loadAdvisorInfo(): Promise<void> {
    this.unified = await this.perfilService.getUnifiedProfile().toPromise();
    console.log('🚀 UnifiedProfile:', this.unified);

    const { bayport, personal } = this.unified;

    this.bayportInfoForm.patchValue({
      executiveCode: bayport.codigoAsesor,
      executiveType: bayport.tipoEjecutivo,
      creationDate: new Date(bayport.fechaCreacion),
      state: bayport.estado,
      regional: bayport.regional,
      branch: bayport.sucursal,
      commertialCoordinator: bayport.coordinadorComercial,
      correo: bayport.correo,
      correo2: personal.correo,
      channelType: bayport.tipoCanal,
      salesChannel: bayport.canalVenta,
      celular: personal.celular,
    });

    [
      'executiveCode', 'executiveType', 'creationDate', 'state',
      'regional', 'branch', 'commertialCoordinator',
      'channelType', 'salesChannel'
    ].forEach(key => this.bayportInfoForm.get(key)!.disable());

    this.codigoAsesor = bayport.codigoAsesor.toString();
    this.tipoEjecDes = this.executiveTypes.find(e => e.indTipoEjecutivo === bayport.tipoEjecutivo)?.descripcion || '';
    this.fechaCrea = new Date(bayport.fechaCreacion);
    this.estado = bayport.estado;
    this.regionalNombre = this.regionalList.find(r => r.codigoRegional === bayport.regional)?.nombre || '';
    this.sucursalNombre = this.sucursales.find(s => s.codigoSucursal === bayport.sucursal)?.nombreSucursal || '';
    this.canalNombre = this.channelTypes.find(c => c.codCnlVta === bayport.tipoCanal)?.nomCnlVta || '';
    this.canalVentaNombre = this.salesChannels.find(s => s.codigoTipo === bayport.canalVenta)?.nombreTipo || '';
    this.nombreRefiere = bayport.coordinadorComercial;

    this.cd.markForCheck();
  }

  onSave(): void {
    if (this.bayportInfoForm.invalid) return;
    this.loadingAdvertencia = true;
    const tasks: Promise<any>[] = [];

    if (this.bayportInfoForm.get('correo')!.dirty) {
      const bayportBody = {
        codigoAsesor: this.unified.bayport.codigoAsesor,
        tipoEjecutivo: this.unified.bayport.tipoEjecutivo,
        fechaCreacion: new Date(this.unified.bayport.fechaCreacion),
        estado: this.unified.bayport.estado,
        regional: this.unified.bayport.regional,
        sucursal: this.unified.bayport.sucursal.toString(),
        coordinadorComercial: this.unified.bayport.coordinadorComercial,
        correo: this.bayportInfoForm.get('correo')!.value,
        tipoCanal: this.unified.bayport.tipoCanal,
        canalVenta: this.unified.bayport.canalVenta,
        celular: this.bayportInfoForm.get('celular')!.value,
      };
      tasks.push(firstValueFrom(this.perfilService.updateInfoBayport(bayportBody)));
    }

    const personalRaw = {
      ...this.unified.personal,
      codigoAsesor: this.unified.bayport.codigoAsesor,
      correo: this.bayportInfoForm.get('correo2')!.dirty ? this.bayportInfoForm.get('correo2')!.value : this.unified.personal.correo,
      celular: this.bayportInfoForm.get('celular')!.dirty ? this.bayportInfoForm.get('celular')!.value : this.unified.personal.celular,
    };
    tasks.push(firstValueFrom(this.perfilService.updateInfoPersonal(personalRaw)));

    Promise.all(tasks)
      .then(() => this.exitoPeticion()
      )
      .catch(err => this.fallaPeticion()
      );
  }

  exitoPeticion() {
    this.colorAdvertencia = "success"
    this.textoAdvertencia = "La información fue guardada exitosamente"
    this.showAdvertencia = true
    this.loadingAdvertencia = false
    this.cd.detectChanges();
    setTimeout(() => {
      this.showAdvertencia = false;
      this.cd.detectChanges();
    }, 5000); // 5 segundos
  }

  fallaPeticion() {
    this.colorAdvertencia = "warning"
    this.textoAdvertencia = "Fallo la actualización"
    this.showAdvertencia = true
    this.loadingAdvertencia = false
    this.cd.detectChanges();
    setTimeout(() => {
      this.showAdvertencia = false;
      this.cd.detectChanges();
    }, 5000); // 5 segundos
  }

}
